import { AbstractControl } from '@angular/forms';
import { FormlyExtension, FormlyFieldConfig } from '@ngx-formly/core';

declare module '@ngx-formly/core' {
  interface FormlyFieldConfig {
    _transformed?: boolean;
  }
}

export function onlyNumbers(
  control: AbstractControl,
): null | { onlyNumbers: boolean } {
  return !control.value || /^[0-9]*$/.test(control.value || '')
    ? null
    : { onlyNumbers: true };
}

export function minItems(
  control: AbstractControl,
  field: FormlyFieldConfig,
): null | { minItems: { min: number } } {
  const min = field.props?.['minItems'] ?? 1;
  const length = Array.isArray(control.value) ? control.value.length : 0;
  return length >= min ? null : { minItems: { min } };
}

export function minlengthValidationMessage(field: FormlyFieldConfig): string {
  return `Muss mindestens ${field.props!.minLength} Zeichen sein`;
}

export function maxlengthValidationMessage(field: FormlyFieldConfig): string {
  return `Kann nicht mehr als ${field.props!.maxLength} Zeichen sein`;
}

export function minValidationMessage(field: FormlyFieldConfig): string {
  return `Muss mehr als ${field.props!.min} sein`;
}

export function maxValidationMessage(field: FormlyFieldConfig): string {
  return `Darf nicht mehr als ${field.props!.max} sein`;
}

export function requiredMessage(): string {
  return 'Erforderlich';
}

export function minItemsValidationMessage(
  _error: unknown,
  field: FormlyFieldConfig,
): string {
  const custom = field.props?.['minItemsMessage'];
  if (custom) {
    return custom;
  }
  const min = field.props?.['minItems'] ?? 1;
  return min === 1
    ? 'Bitte geben Sie mindestens einen Eintrag an'
    : `Bitte geben Sie mindestens ${min} Einträge an`;
}

/**
 * Recursively transforms form fields to include Angular Material labels and structure.
 *
 * @private
 * @param {FormlyFieldConfig} field - Input field configuration.
 * @param {boolean} isInsideRepeating - Whether the field is nested inside a repeating array item.
 * @returns {void}
 */
function transformField(
  field: FormlyFieldConfig,
  isInsideRepeating = false,
): void {
  if (field._transformed) {
    return;
  }

  field._transformed = true;

  if (isInsideRepeating && field.props) {
    field.props['isChildLabel'] = true;
  }

  if (
    !isInsideRepeating &&
    field.props?.label &&
    field.props.label.length > 0 &&
    field.type &&
    field.type !== 'file' &&
    field.type !== 'stepper' &&
    field.type !== 'customLabel' &&
    field.type !== 'checkbox' &&
    field.type !== 'repeatDialog' &&
    !field.fieldGroup
  ) {
    const customLabel = field.props.label;
    const className = field.className;

    // Deep clone props for wrapped field to avoid object reference mutation
    const wrappedField: FormlyFieldConfig = {
      ...field,
      props: field.props ? { ...field.props } : undefined,
      _transformed: true,
    };

    delete wrappedField.className;
    if (wrappedField.props) {
      delete wrappedField.props.label;
    }

    wrappedField.className = 'custom-field';

    // Replace current field with a fieldGroup containing custom label + field
    field.fieldGroup = [
      {
        props: {
          label: customLabel,
          required: field.props.required,
          tooltip: field.props['tooltip'],
        },
        type: 'customLabel',
        expressions: field.expressions,
        hide: field.hide,
        _transformed: true,
      },
      wrappedField,
    ];

    field.fieldGroupClassName = className;
    delete field.type;
    delete field.key;
    delete field.props;
    delete field.expressions;
    delete field.hide;
  } else if (!isInsideRepeating && field.type === 'checkbox') {
    const wrappedField: FormlyFieldConfig = {
      ...field,
      props: field.props ? { ...field.props } : undefined,
      _transformed: true,
    };

    field.className = 'custom-field';
    field.fieldGroup = [wrappedField];
    field.fieldGroupClassName = wrappedField.className;

    delete field.type;
    delete field.key;
    delete field.props;
  }

  if (field.fieldGroup) {
    field.fieldGroup.forEach((f) => transformField(f, isInsideRepeating));
  }

  if (field.fieldArray) {
    if (typeof field.fieldArray === 'object') {
      const fieldArrayObj = field.fieldArray as FormlyFieldConfig;
      if (Array.isArray(fieldArrayObj.fieldGroup)) {
        fieldArrayObj.fieldGroup.forEach((f) => transformField(f, true));
      } else {
        transformField(fieldArrayObj, true);
      }
    }
  }
}

export const transformFieldExtension: FormlyExtension = {
  prePopulate(field) {
    transformField(field);
  },
};

export const uppercaseExtension: FormlyExtension = {
  prePopulate(field) {
    if (!field.props?.['uppercase']) {
      return;
    }
    field.parsers = [
      ...(field.parsers || []),
      (value: string) => value.toUpperCase(),
    ];
  },
};
